import {
  Component,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  Output,
  EventEmitter,
  HostListener,
} from '@angular/core';
import { HomeComponent } from '../home/home.component';
import { BeadCanvas } from '../peditor/beadcanvas';
import { Action } from '../peditor/patternmodelcontroller';
import { Point } from '../peditor/point';
import { Size } from '../peditor/size';
import { DEFAULT_ZOOM_FACTOR } from '../helpers/constants';
import { OnboardingService } from '../services/onboarding.service';

export interface CanvasViewportState {
  scrollLeft: number;
  scrollTop: number;
  zoomFactor: number;
}

@Component({
  selector: 'app-bead-canvas',
  templateUrl: './bead-canvas.component.html',
})
export class BeadCanvasComponent implements OnInit {
  @ViewChild('canvasContainer', { static: true })
  private canvasContainer: ElementRef<HTMLDivElement>;

  @ViewChild('canvas', { static: true })
  private canvas: ElementRef<HTMLCanvasElement>;

  @ViewChild('beadSelImg', { static: true })
  private beadSelImg: ElementRef<HTMLImageElement>;

  @ViewChild('beadEmptyImg', { static: true })
  private beadEmptyImg: ElementRef<HTMLImageElement>;

  @ViewChild('beadMetallicImg', { static: true })
  private beadMetallicImg: ElementRef<HTMLImageElement>;

  @ViewChild('beadUnknownImg', { static: true })
  private beadUnknownImg: ElementRef<HTMLImageElement>;

  @ViewChild('beadCrystalImg', { static: true })
  private beadCrystalImg: ElementRef<HTMLImageElement>;

  @ViewChild('beadTransparentImg', { static: true })
  private beadTransparentImg: ElementRef<HTMLImageElement>;

  @ViewChild('beadOpaqueImg', { static: true })
  private beadOpaqueImg: ElementRef<HTMLImageElement>;

  @ViewChild('beadMatteImg', { static: true })
  private beadMatteImg: ElementRef<HTMLImageElement>;

  @ViewChild('realBeadImagesImg', { static: true })
  private realBeadImagesImg: ElementRef<HTMLImageElement>;

  public innerWidth: any;

  @Output()
  onActionFired: EventEmitter<Action> = new EventEmitter<Action>();

  public _bcanvas: BeadCanvas;

  @Input()
  set bcanvas(bc: BeadCanvas) {
    this._bcanvas = bc;
  }

  @Input()
  home: HomeComponent;

  public maxWindowSize: number = 2000;

  constructor(public onboardingService: OnboardingService) {}

  async ngOnInit() {
    var beadTypeImages: HTMLImageElement[] = new Array(
      this.beadEmptyImg.nativeElement,
      this.beadMetallicImg.nativeElement,
      this.beadUnknownImg.nativeElement,
      this.beadCrystalImg.nativeElement,
      this.beadTransparentImg.nativeElement,
      this.beadOpaqueImg.nativeElement,
      this.beadMatteImg.nativeElement,
    );

    this._bcanvas.initCanvas(
      this.canvas.nativeElement,
      this.beadSelImg.nativeElement,
      beadTypeImages,
      this.realBeadImagesImg.nativeElement,
      this.onActionFired,
    );

    this.resizeCanvasContainer();
  }

  public resetPatternPosition = (): void => {
    const zoomFactor = this.getBeadCanvas().getZoomFactor();
    const patternHandleHeight = this.getBeadCanvas().patternHandleHeight;
    const patternSize = this.getBeadCanvas().getPatternSize();
    const canvasContainerWidth = this.canvasContainer.nativeElement.clientWidth;
    const canvas = this.canvas.nativeElement;

    // Calculate x position to center the pattern in the visible viewport
    const patternWidth = patternSize.width * zoomFactor;
    const canvasCenter = canvas.width / 2;
    const containerScrollLeft = this.canvasContainer.nativeElement.scrollLeft;
    const visibleCenter = containerScrollLeft + canvasContainerWidth / 2;

    // Calculate the offset needed to center the pattern in the visible area
    let x = (visibleCenter - canvasCenter) / zoomFactor;

    // If pattern is wider than visible area, align to left edge with margin
    if (patternWidth > canvasContainerWidth) {
      const leftEdge = containerScrollLeft;
      x = (leftEdge - canvasCenter) / zoomFactor / zoomFactor;
    }

    // Calculate y position: position at top with spacing from toolbar
    const y = patternHandleHeight + 50 / zoomFactor;

    this._bcanvas.getModel().setPosition(new Point(x, y));

    // CRITICAL: Must repaint after changing position so the canvas reflects the new position
    this._bcanvas.repaint();

    // Scroll to show the pattern
    this._bcanvas.scrollCanvasContainerToPatternPosition();
  };

  public resetRotation = (): void => {
    this._bcanvas.getModel().setRotation(0);
    this._bcanvas.rotatedPattern(false);
  };

  public resetZoom = (): void => {
    this._bcanvas.zoom(DEFAULT_ZOOM_FACTOR);
  };

  public captureViewportState(): CanvasViewportState | null {
    if (!this.canvasContainer?.nativeElement || !this._bcanvas) {
      return null;
    }

    return {
      scrollLeft: this.canvasContainer.nativeElement.scrollLeft,
      scrollTop: this.canvasContainer.nativeElement.scrollTop,
      zoomFactor: this._bcanvas.getZoomFactor(),
    };
  }

  public restoreViewportState(state: CanvasViewportState | null): void {
    if (!state || !this.canvasContainer?.nativeElement || !this._bcanvas) {
      this._bcanvas?.scrollCanvasContainerToPatternPosition();
      return;
    }

    if (Math.abs(state.zoomFactor - this._bcanvas.getZoomFactor()) > 0.01) {
      this._bcanvas.zoom(state.zoomFactor);
      this._bcanvas.repaint();
    }

    requestAnimationFrame(() => {
      this.canvasContainer.nativeElement.scrollLeft =
        state.scrollLeft ?? this.canvasContainer.nativeElement.scrollLeft;
      this.canvasContainer.nativeElement.scrollTop =
        state.scrollTop ?? this.canvasContainer.nativeElement.scrollTop;
    });
  }

  resizeCanvasContainer() {
    if (this.home) {
      this.canvasContainer.nativeElement.style.width =
        window.innerWidth - (this.home.rightSidebarOpen ? 320 : 0) + 'px';
      this.canvasContainer.nativeElement.style.height =
        window.innerHeight - this.home.appToolbarHeight + 'px';

      const max = Math.max(window.innerWidth, window.innerHeight);
      if (max > this.maxWindowSize) {
        this.maxWindowSize = max;
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.resizeCanvasContainer();
  }

  public getBeadCanvas(): BeadCanvas {
    return this._bcanvas;
  }
}
